import { GroupOrderingMode, GroupOrderingNameType, TaxRoundingMode, TaxRoundingModeHelper, UserRight, } from '@biteinc/enums';
export const commonSettingsSchema = {
    // Location Model Settings
    // COMMON SETTINGS
    brandName: {
        displayName: 'Brand name',
        type: 'shortString',
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'If specified, this name will show up in receipts or emails sent on behalf of this location to the guest. Defaults to org name.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    showHiddenItems: {
        displayName: 'Show Hidden Items',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Show hidden (86d) items on the menu as unavailable. If disabled, hidden items will not be shown on the menu.',
        },
    },
    quickCheckoutEnabled: {
        displayName: 'Enable Quick Checkout',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    showNutritionInfo: {
        displayName: 'Show Nutrition Info on Menu',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    calorieSuffix: {
        displayName: 'Calorie Suffix',
        type: 'shortString',
        tabId: 'common-settings-v2',
        exclusiveTo: 'gcn',
        tooltip: {
            text: 'This is the text that will display after the calorie value. Defaults to Cals.',
        },
        conditions: [
            {
                field: 'showNutritionInfo',
                values: [true],
            },
        ],
        writeRight: UserRight.ManageChannelSettings,
    },
    groupOrderingMode: {
        displayName: 'Group Ordering Mode',
        type: 'stringEnum',
        enum: GroupOrderingMode,
        required: true,
        ui: 'dropdown',
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    groupOrderingNameType: {
        displayName: 'Group Ordering Naming Type',
        type: 'stringEnum',
        enum: GroupOrderingNameType,
        required: true,
        tabId: 'common-settings-v2',
        ui: 'dropdown',
        writeRight: UserRight.ManageChannelSettings,
        conditions: [
            {
                field: 'groupOrderingMode',
                values: [GroupOrderingMode.AskGroupOrderOptional, GroupOrderingMode.AskGroupOrderRequired],
            },
        ],
    },
    showGuestSurvey: {
        displayName: 'Show Guest Survey',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    autoScrollCustomization: {
        displayName: 'Auto-Scroll the customization flow',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'Automatically scroll the customization view when a modifier group is done being customized.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    allowSpecialRequests: {
        displayName: 'Allow Special requests',
        type: 'bool',
        required: true,
        tooltip: {
            text: 'Allow guests to type in special requests for each items.',
        },
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    deemphasizeSpecialRequests: {
        displayName: 'De-emphasize Special requests',
        type: 'bool',
        required: true,
        tooltip: {
            text: "Hide special requests behind expandable 'Modify Ingredients' button.",
        },
        conditions: [
            {
                field: 'allowSpecialRequests',
                values: [true],
            },
        ],
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    useSeparateScreenForEachModGroupInFullScreenFlow: {
        displayName: 'Separate nested pickers in Full Screen Flow',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    marketingConsentCheckbox: {
        displayName: 'Marketing Consent Checkbox',
        type: 'object',
        required: true,
        isNullable: true,
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        fields: {
            selectedByDefault: {
                displayName: 'Check by default',
                type: 'bool',
                required: true,
                defaultValue: true,
            },
            isRequiredToContinue: {
                displayName: 'Is required to continue',
                type: 'bool',
                required: true,
                defaultValue: false,
            },
        },
    },
    enableBlockedWords: {
        displayName: 'Block bad words',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        defaultValue: true,
        tooltip: {
            text: 'Do not allow blocked words in guest name and special requests fields',
        },
    },
    customBlockedWords: {
        displayName: 'Custom blocked words',
        type: 'keyString',
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'Comma separated list of blocked words. Only used if "Block bad words" is enabled.',
        },
        conditions: [
            {
                field: 'enableBlockedWords',
                values: [true],
            },
        ],
    },
    setImagesForNewSyncedImagelessMods: {
        displayName: 'Automatically Set Images for New Imageless Mods',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'When enabled Bite will find the last updated mod with the same name and images. These images will be used for the newly synced mod.',
        },
    },
    maxNumberOfLoyaltySignInAttempts: {
        displayName: 'Max Number of Loyalty Sign-In Attempts',
        type: 'int',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'The maximum number of attempts a guest can make to sign in to their loyalty account before they are locked out.',
        },
        isNullable: true,
        min: 1,
    },
    maxNumberOfGiftCardAccessAttempts: {
        displayName: 'Max Number of Gift Card Access Attempts',
        type: 'int',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'The maximum number of failed attempts a guest can make to access a gift card (balance check, charge, or tender lookup) before further attempts are rejected for the rest of the order.',
        },
        isNullable: true,
        min: 1,
    },
    displayIndividualTaxes: {
        displayName: 'Display Individual Taxes',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'Brink & Deliverect Only! Will show each the name and amount of each separate tax applied to the order.',
        },
    },
    capitalizeGuestName: {
        displayName: 'Capitalize Guest Name Everywhere',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'Capitalize guest name everywhere in the app and on receipts.',
        },
    },
    useFirstNameOnlyForGuestNameFromLoyalty: {
        displayName: 'Use First Name Only for Guest Name when fetched from Loyalty',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'If enabled, the first name only will be used for the guest name when fetched from loyalty.',
        },
    },
    allowZeroDollarOrders: {
        displayName: 'Allow $0 Orders',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'Allow orders with a subtotal of $0.00 to be placed. Fully discounted orders with a positive subtotal are not affected by this setting.',
        },
    },
    taxRoundingMode: {
        displayName: 'Tax Rounding Mode',
        type: 'stringEnum',
        enum: TaxRoundingMode,
        enumNameHelper: TaxRoundingModeHelper.name,
        required: true,
        ui: 'dropdown',
        tabId: 'common-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    useVatPricing: {
        displayName: 'VAT Pricing',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'No taxes on the kiosk, the receipt or sent to the POS',
            isWarning: true,
        },
    },
    holisticItemCapitalization: {
        displayName: 'Holistic Item Capitalization',
        type: 'bool',
        required: true,
        tabId: 'common-settings-v2',
        tooltip: {
            text: 'Brink Only! If enabled, Bite will use the same capitalization as the POS for items and modifiers if one word is fully capitalized and the rest are title case.',
            isWarning: true,
        },
    },
};
//# sourceMappingURL=settings_common_schema.js.map